import React, { useCallback } from 'react';

import { useImmer } from 'use-immer';

import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';

import { setZoneZipContext } from '../../../../../common/CompanyPane/Company.slice';

import {
  AutoSelectOption,
  BaseCompanyZoneZipSelect,
  createZoneZipSelectState,
  ZoneZipSelectState
} from '../../../../../common/CompanyPane/CompanySelected/BaseCompanyZipZoneSelect';
import { ZipZoneSelectComponentProps } from '../../../../../common/CompanyPane/CompanySelected/zipZoneSelectContext';
import { selectPhoneCallContext } from '../../../StPortalPhoneCall.slice';

export const CompanyZoneZipSelect = (props: ZipZoneSelectComponentProps): React.ReactElement => {
  const { onCompanySwitchConfirmed, companyContext, zoneZipContext } = props;

  const dispatch = useAppDispatch();
  const phoneCallContext = useAppSelector(selectPhoneCallContext);

  const [zoneZipSelectState, setZoneZipSelectState] = useImmer<ZoneZipSelectState>(createZoneZipSelectState());

  /**
   * Set CompanyContext `selectedZone` & `selectedZipOrPostal`
   *
   * Need this override in order to set overrideZipMatch: null
   */
  const handleSetSelectedOption = useCallback(
    (opt: AutoSelectOption | null) => {
      const selectedZone = opt?.zone || null;
      const selectedZipOrPostal = opt?.zipCode || null;
      if (
        zoneZipContext?.selectedZone?.id !== selectedZone?.id ||
        zoneZipContext?.selectedZipOrPostal !== selectedZipOrPostal
      ) {
        dispatch(setZoneZipContext({ selectedZone, selectedZipOrPostal, overrideZipMatch: null }));
      }
    },
    [dispatch, zoneZipContext?.selectedZipOrPostal, zoneZipContext?.selectedZone?.id]
  );

  /**
   * Need this override to set overrideZipMatch: true
   */
  const handleOverrideZipOrPostalYES = useCallback(
    (outOfServiceAreaZip?: string) => {
      if (outOfServiceAreaZip) {
        dispatch(
          setZoneZipContext({ selectedZipOrPostal: outOfServiceAreaZip, selectedZone: null, overrideZipMatch: true })
        );
      }
    },
    [dispatch]
  );

  const handleOverrideZipOrPostalNO = useCallback(
    (outOfServiceAreaZip?: string) => {
      if (outOfServiceAreaZip) {
        // NOTE: we still set `selectedZipOrPostal`, so that we have the zip to display. What's important is that we set
        // `overrideZipMatch` to false
        dispatch(
          setZoneZipContext({ selectedZipOrPostal: outOfServiceAreaZip, selectedZone: null, overrideZipMatch: false })
        );
      } else {
        dispatch(
          setZoneZipContext({
            ...(zoneZipContext ? zoneZipContext : { selectedZipOrPostal: null, selectedZone: null }),
            overrideZipMatch: false
          })
        );
      }
    },
    [dispatch, zoneZipContext]
  );

  return (
    <BaseCompanyZoneZipSelect
      onCompanySwitchConfirmed={onCompanySwitchConfirmed}
      companyContext={companyContext}
      zoneZipContext={zoneZipContext}
      externalZipOrPostal={phoneCallContext.location?.zipOrPostal}
      zoneZipSelectState={zoneZipSelectState}
      setZoneZipSelectState={setZoneZipSelectState}
      onSetSelectedOption={handleSetSelectedOption}
      onOverrideZipOrPostalYES={handleOverrideZipOrPostalYES}
      onOverrideZipOrPostalNO={handleOverrideZipOrPostalNO}
    />
  );
};
